import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNowPlaying } from "../context/NowPlayingContext";
import ZoomableImage from "../components/ZoomableImage";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { ChevronRight, Droplet, Sun, Thermometer, ArrowLeft, BookOpen, Sparkles, Cloud, MapPin, Sprout, Leaf, Scissors, AlertTriangle, Bug, FlaskConical, Wind, Music, Play, Pause, Lock } from "lucide-react";

function WeatherCard({ weather }) {
  if (!weather || weather.temp_c == null) return null;
  const temp = Math.round(weather.temp_c);
  const humidity = weather.humidity != null ? Math.round(weather.humidity) : null;
  const condition = weather.condition || "—";
  const place = [weather.city, weather.country].filter(Boolean).join(", ");
  return (
    <div
      className="mt-4 glass-card p-4 fade-up stagger-3"
      data-testid="weather-card"
      style={{ borderColor: "rgba(76,175,80,0.35)" }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Cloud size={16} className="text-[#4CAF50]" />
          <p className="text-[11px] uppercase tracking-wider text-muted-soft">Live conditions</p>
        </div>
        {place && (
          <div className="flex items-center gap-1 text-[11px] text-muted-soft">
            <MapPin size={12} className="text-[#4CAF50] opacity-80" />
            <span data-testid="weather-location">{place}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-white text-xl font-bold">
            <Thermometer size={14} className="text-[#FF8F00]" />
            <span data-testid="weather-temp">{temp}°C</span>
          </div>
          <p className="text-[10px] text-muted-soft uppercase tracking-wider mt-0.5">Temp</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-white text-xl font-bold">
            <Droplet size={14} className="text-[#4CAF50]" />
            <span data-testid="weather-humidity">{humidity != null ? `${humidity}%` : "—"}</span>
          </div>
          <p className="text-[10px] text-muted-soft uppercase tracking-wider mt-0.5">Humidity</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-white text-sm font-semibold truncate">
            <Sun size={14} className="text-[#FFD700]" />
            <span data-testid="weather-condition" className="truncate">{condition}</span>
          </div>
          <p className="text-[10px] text-muted-soft uppercase tracking-wider mt-0.5">Sky</p>
        </div>
      </div>
    </div>
  );
}

function CarePill({ emoji, short, full, testid, icon: Icon }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="glass-pill solid cursor-help"
            style={{ fontSize: 13 }}
            data-testid={testid}
            aria-label={full || short}
          >
            <span style={{ fontSize: 14 }}>{emoji}</span>
            <span className="font-semibold">{short}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={6}
          className="max-w-[260px] text-[12.5px] leading-snug font-medium"
          style={{
            background: "rgba(8,12,18,0.96)",
            color: "#fff",
            border: "1px solid rgba(76,175,80,0.5)",
            boxShadow: "0 8px 22px rgba(0,0,0,0.55), 0 0 14px rgba(76,175,80,0.25)",
            padding: "8px 12px",
          }}
        >
          <span className="inline-flex items-start gap-1.5">
            {Icon ? <Icon size={13} className="text-[#A5D6A7] mt-[2px] flex-shrink-0" /> : null}
            <span>{full}</span>
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ConfidenceRing({ value = 0, color = "#4CAF50" }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" className="confidence-ring">
      <circle cx="28" cy="28" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
      <circle
        className="fill"
        cx="28" cy="28" r={r}
        fill="none"
        stroke={color}
        strokeWidth="4"
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 28 28)"
        style={{ filter: `drop-shadow(0 0 6px ${color})` }}
      />
      <text x="28" y="32" textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">
        {Math.round(value)}%
      </text>
    </svg>
  );
}

export default function IdentifyResultScreen() {
  const { scanId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scan, setScan] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!scan);

  // Music recommendation state
  const [music, setMusic] = useState(null);
  const { track: npTrack, isPlaying, play: playTrack } = useNowPlaying();
  const activeId = npTrack?.id;

  useEffect(() => {
    if (scan) return;
    (async () => {
      try {
        const { data } = await api.get(`/scans/${scanId}`);
        setScan({ ...data, result: data.result });
      } catch {} finally { setLoading(false); }
    })();
  }, [scan, scanId]);

  // Fetch music recommendation once we have a scientific_name
  const topScientific = scan?.result?.species?.[0]?.scientific_name;
  useEffect(() => {
    if (!topScientific) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/music/recommend?scientific_name=${encodeURIComponent(topScientific)}`
        );
        setMusic(data);
      } catch (err) {
        console.warn("[Identify] music recs failed:", err);
      }
    })();
  }, [topScientific]);

  if (loading) {
    return (
      <Shell section="identify_result">
        <div className="glass-card p-8 mt-10 text-center">
          <span className="inline-block w-3 h-3 rounded-full bg-[#4CAF50] dot-pulse" />
          <p className="text-soft text-sm mt-4">Loading result…</p>
        </div>
      </Shell>
    );
  }

  const result = scan?.result || {};
  if (result.error === "not_a_plant") {
    return (
      <Shell section="identify_result">
        <div className="glass-card p-6 mt-6 text-center fade-up">
          <h2 className="text-xl font-bold mb-2">No plant detected</h2>
          <p className="text-soft text-sm">{result.message || "Please try with a clearer plant photo."}</p>
          <button onClick={() => navigate("/identify")} className="btn-primary mt-5">Try again</button>
        </div>
      </Shell>
    );
  }

  const species = result.species || [];
  const top = species[0];
  const care = result.care || {};
  const ringColor = (top?.confidence || 0) > 80 ? "#4CAF50" : (top?.confidence || 0) > 50 ? "#FF8F00" : "#D32F2F";

  // Short labels for the Quick Care pills (AI may return long sentences)
  const shortPill = (s, max = 28) => {
    if (!s) return "";
    const first = String(s).split(/[.;]/)[0].trim();
    return first.length > max ? first.slice(0, max - 1).trim() + "…" : first;
  };

  return (
    <Shell section="identify_result">
      <button onClick={() => navigate("/home")} className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline" data-testid="back-button">
        <ArrowLeft size={14} /> Home
      </button>
      <div className="glass-card-strong overflow-hidden fade-up" data-testid="identify-result-card">
        {scan?.image_data && (
          <ZoomableImage
            src={scan.image_data}
            alt="Scanned plant"
            thumbHeight={260}
            testid="scan-image"
          />
        )}
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-shadow-green" data-testid="result-common-name">
                {top?.common_name || "Unknown species"}
              </h1>
              <p className="italic text-[#4CAF50] mt-1 opacity-80 text-[15px]">
                {top?.scientific_name}
              </p>
              {top?.family && <span className="glass-pill mt-3 inline-flex">{top.family}</span>}
            </div>
            {top?.confidence != null && <ConfidenceRing value={top.confidence} color={ringColor} />}
          </div>

          {species.length > 1 && (
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-2">Other possibilities</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {species.slice(1).map((s, i) => (
                  <div key={i} className="glass-card px-3 py-2 flex-shrink-0">
                    <p className="text-sm font-semibold">{s.common_name}</p>
                    <p className="text-[11px] text-muted-soft italic">{s.scientific_name} · {Math.round(s.confidence)}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {care.watering && (
        <div className="mt-5">
          {/* Quick Care Pills with emoji icons per spec — full text shown on hover/tap */}
          <div className="flex gap-2 mb-3 flex-wrap fade-up stagger-1">
            <CarePill
              emoji="☀️"
              icon={Sun}
              short={shortPill(care.sunlight?.type) || "Bright indirect"}
              full={care.sunlight?.type || "Bright, indirect light"}
              testid="quick-pill-light"
            />
            <CarePill
              emoji="💧"
              icon={Droplet}
              short={shortPill(care.watering?.frequency) || "Weekly"}
              full={care.watering?.frequency || "Water weekly"}
              testid="quick-pill-water"
            />
            <CarePill
              emoji="🌡️"
              icon={Thermometer}
              short={shortPill(care.temperature?.ideal_celsius, 18) || "18–24°C"}
              full={care.temperature?.ideal_celsius || "18–24°C"}
              testid="quick-pill-temp"
            />
            {care.humidity?.ideal_percentage && (
              <CarePill
                emoji="💨"
                icon={Wind}
                short={shortPill(care.humidity.ideal_percentage, 18)}
                full={care.humidity.ideal_percentage}
                testid="quick-pill-humidity"
              />
            )}
          </div>

          {/* Recommended music for this freshly-scanned plant */}
          {music && music.tracks && music.tracks.length > 0 && (
            <div
              className="rounded-2xl p-5 mb-4 fade-up stagger-2"
              style={{
                background: "rgba(8, 14, 10, 0.85)",
                border: "1px solid rgba(76,175,80,0.45)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.6), 0 0 18px rgba(76,175,80,0.22)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
              }}
              data-testid="identify-music-recommendations"
            >
              <div className="flex items-start gap-3 mb-3">
                <span
                  className="inline-flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0"
                  style={{ background: "rgba(76,175,80,0.22)", boxShadow: "var(--glow-green)" }}
                >
                  <Music size={18} className="text-[#4CAF50]" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-muted-soft">Recommended music for this plant</p>
                  <h3 className="text-base font-bold leading-tight mt-0.5" style={{ color: "#A5D6A7", textShadow: "0 0 14px rgba(76,175,80,0.45)" }}>
                    {music.primary_benefit}
                  </h3>
                  <p className="text-[11px] text-soft mt-0.5">
                    Tuned to <b className="text-white">{music.frequencies.join(" + ")}</b>
                  </p>
                </div>
              </div>

              {music.reason && (
                <p className="text-sm leading-relaxed text-soft mb-4">{music.reason}</p>
              )}

              <div className="space-y-2" data-testid="identify-music-tracks">
                {music.tracks.map((t) => {
                  const isActive = activeId === t.id && isPlaying;
                  const isLocked = t.access_level === "preview";
                  return (
                    <div
                      key={t.id}
                      className="flex items-center gap-3 p-2.5 rounded-xl"
                      style={{
                        background: isActive ? "rgba(76,175,80,0.10)" : "rgba(8,14,10,0.55)",
                        border: `1px solid ${isActive ? "rgba(76,175,80,0.5)" : "rgba(255,255,255,0.06)"}`,
                      }}
                      data-testid={`identify-music-${t.id}`}
                    >
                      <button
                        onClick={() => playTrack(t)}
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all hover:scale-105"
                        style={{
                          background: isActive
                            ? "linear-gradient(135deg,#4CAF50,#66BB6A)"
                            : "rgba(76,175,80,0.18)",
                          border: `1px solid ${isActive ? "rgba(76,175,80,0.7)" : "rgba(76,175,80,0.35)"}`,
                          boxShadow: isActive ? "0 0 12px rgba(76,175,80,0.5)" : "none",
                        }}
                        data-testid={`identify-play-${t.id}`}
                        aria-label={isActive ? `Pause ${t.title}` : `Play ${t.title}`}
                      >
                        {isActive ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-[#4CAF50] ml-0.5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold truncate text-sm">{t.title}</p>
                          {isLocked && <Lock size={10} className="text-[#FFD54F] flex-shrink-0" />}
                        </div>
                        <p className="text-[10px] text-muted-soft">
                          {t.frequency} · {t.duration}
                          {isLocked && <span className="text-[#FFD54F] ml-1">· 30s preview</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => navigate("/music")}
                className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold py-2 rounded-xl transition-colors hover:bg-white/5"
                style={{ color: "#A5D6A7", border: "1px solid rgba(76,175,80,0.35)" }}
                data-testid="identify-music-explore"
              >
                Explore full music library <ChevronRight size={12} />
              </button>
            </div>
          )}

          <div className="glass-card-strong p-5 fade-up stagger-2" data-testid="care-summary">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={18} className="text-[#4CAF50]" />
              <h3 className="text-lg font-semibold">Complete Care Guide</h3>
            </div>

            <div className="space-y-4 text-sm">
              {/* Watering */}
              {care.watering && (
                <div className="care-block" data-testid="care-watering">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Droplet size={14} className="text-[#4CAF50]" />
                    <h4 className="font-semibold text-white">Watering</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    {care.watering.frequency && <p><span className="text-muted-soft">Frequency:</span> {care.watering.frequency}</p>}
                    {care.watering.method && <p><span className="text-muted-soft">Method:</span> {care.watering.method}</p>}
                    {care.watering.signs_overwatering && <p><span className="text-[#FF8F00]">Overwatered:</span> {care.watering.signs_overwatering}</p>}
                    {care.watering.signs_underwatering && <p><span className="text-[#FF8F00]">Underwatered:</span> {care.watering.signs_underwatering}</p>}
                  </div>
                </div>
              )}

              {/* Sunlight */}
              {care.sunlight && (
                <div className="care-block" data-testid="care-sunlight">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Sun size={14} className="text-[#FFD700]" />
                    <h4 className="font-semibold text-white">Light</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    {care.sunlight.type && <p><span className="text-muted-soft">Type:</span> {care.sunlight.type}</p>}
                    {care.sunlight.hours_per_day && <p><span className="text-muted-soft">Hours/day:</span> {care.sunlight.hours_per_day}</p>}
                    {care.sunlight.placement_tips && <p><span className="text-muted-soft">Placement:</span> {care.sunlight.placement_tips}</p>}
                  </div>
                </div>
              )}

              {/* Soil */}
              {care.soil && (care.soil.type || care.soil.ph_range || care.soil.drainage) && (
                <div className="care-block" data-testid="care-soil">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Sprout size={14} className="text-[#8D6E63]" />
                    <h4 className="font-semibold text-white">Soil</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    {care.soil.type && <p><span className="text-muted-soft">Type:</span> {care.soil.type}</p>}
                    {care.soil.ph_range && <p><span className="text-muted-soft">pH range:</span> {care.soil.ph_range}</p>}
                    {care.soil.drainage && <p><span className="text-muted-soft">Drainage:</span> {care.soil.drainage}</p>}
                  </div>
                </div>
              )}

              {/* Temperature */}
              {care.temperature && (care.temperature.ideal_celsius || care.temperature.min_celsius) && (
                <div className="care-block" data-testid="care-temperature">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Thermometer size={14} className="text-[#FF8F00]" />
                    <h4 className="font-semibold text-white">Temperature</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    {care.temperature.ideal_celsius && <p><span className="text-muted-soft">Ideal:</span> {care.temperature.ideal_celsius}</p>}
                    {(care.temperature.min_celsius || care.temperature.max_celsius) && (
                      <p><span className="text-muted-soft">Tolerance:</span> {care.temperature.min_celsius}{care.temperature.max_celsius ? ` – ${care.temperature.max_celsius}` : ""}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Humidity */}
              {care.humidity && (care.humidity.ideal_percentage || care.humidity.tips) && (
                <div className="care-block" data-testid="care-humidity">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Wind size={14} className="text-[#4FC3F7]" />
                    <h4 className="font-semibold text-white">Humidity</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    {care.humidity.ideal_percentage && <p><span className="text-muted-soft">Ideal:</span> {care.humidity.ideal_percentage}</p>}
                    {care.humidity.tips && <p><span className="text-muted-soft">Tips:</span> {care.humidity.tips}</p>}
                  </div>
                </div>
              )}

              {/* Fertiliser */}
              {care.fertiliser && (care.fertiliser.type || care.fertiliser.frequency) && (
                <div className="care-block" data-testid="care-fertiliser">
                  <div className="flex items-center gap-2 mb-1.5">
                    <FlaskConical size={14} className="text-[#9C27B0]" />
                    <h4 className="font-semibold text-white">Fertiliser</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    {care.fertiliser.type && <p><span className="text-muted-soft">Type:</span> {care.fertiliser.type}</p>}
                    {care.fertiliser.frequency && <p><span className="text-muted-soft">Frequency:</span> {care.fertiliser.frequency}</p>}
                    {care.fertiliser.season && <p><span className="text-muted-soft">Season:</span> {care.fertiliser.season}</p>}
                  </div>
                </div>
              )}

              {/* Pruning */}
              {care.pruning && (care.pruning.when || care.pruning.how) && (
                <div className="care-block" data-testid="care-pruning">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Scissors size={14} className="text-[#4CAF50]" />
                    <h4 className="font-semibold text-white">Pruning</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    {care.pruning.when && <p><span className="text-muted-soft">When:</span> {care.pruning.when}</p>}
                    {care.pruning.how && <p><span className="text-muted-soft">How:</span> {care.pruning.how}</p>}
                  </div>
                </div>
              )}

              {/* Propagation */}
              {care.propagation && (care.propagation.method || care.propagation.best_season) && (
                <div className="care-block" data-testid="care-propagation">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Leaf size={14} className="text-[#4CAF50]" />
                    <h4 className="font-semibold text-white">Propagation</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    {care.propagation.method && <p><span className="text-muted-soft">Method:</span> {care.propagation.method}</p>}
                    {care.propagation.best_season && <p><span className="text-muted-soft">Best season:</span> {care.propagation.best_season}</p>}
                    {care.propagation.difficulty && <p><span className="text-muted-soft">Difficulty:</span> {care.propagation.difficulty}</p>}
                  </div>
                </div>
              )}

              {/* Common problems */}
              {Array.isArray(care.common_problems) && care.common_problems.filter(Boolean).length > 0 && (
                <div className="care-block" data-testid="care-problems">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Bug size={14} className="text-[#FF8F00]" />
                    <h4 className="font-semibold text-white">Common Problems</h4>
                  </div>
                  <ul className="pl-6 space-y-1 text-soft list-disc list-inside marker:text-[#4CAF50]">
                    {care.common_problems.filter(Boolean).map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Toxicity warning */}
              {care.toxicity && (care.toxicity.toxic_to_pets || care.toxicity.toxic_to_humans) && (
                <div
                  className="care-block rounded-xl p-3"
                  style={{ background: "rgba(211,47,47,0.10)", border: "1px solid rgba(211,47,47,0.35)" }}
                  data-testid="care-toxicity"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <AlertTriangle size={14} className="text-[#FF6B6B]" />
                    <h4 className="font-semibold text-white">Toxicity warning</h4>
                  </div>
                  <div className="pl-6 space-y-1 text-soft">
                    <p>
                      {care.toxicity.toxic_to_pets && "Toxic to pets"}
                      {care.toxicity.toxic_to_pets && care.toxicity.toxic_to_humans && " · "}
                      {care.toxicity.toxic_to_humans && "Toxic to humans"}
                    </p>
                    {care.toxicity.details && <p className="text-muted-soft">{care.toxicity.details}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* PLATINUM Deep-Dive Insights ------------------------------------ */}
          {user?.tier === "platinum" && (Array.isArray(care.companion_plants) && care.companion_plants.filter(Boolean).length > 0 || (care.toxicity && !care.toxicity.toxic_to_pets && !care.toxicity.toxic_to_humans) || care.propagation || care.fertiliser) && (
            <div
              className="glass-card-strong mt-5 p-5 fade-up stagger-3"
              style={{
                background: "rgba(8,12,18,0.62)",
                border: "1px solid rgba(255,213,79,0.45)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 22px rgba(255,213,79,0.18)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
              }}
              data-testid="platinum-insights-identify"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={18} className="text-[#FFD54F]" />
                <p
                  className="text-[11px] uppercase tracking-wider font-extrabold"
                  style={{ color: "#FFE082", textShadow: "0 1px 6px rgba(0,0,0,0.7)" }}
                >
                  Platinum deep-dive
                </p>
              </div>
              <h3
                className="text-lg font-extrabold mb-4 text-white"
                style={{ textShadow: "0 2px 10px rgba(0,0,0,0.7)" }}
              >
                More about this plant
              </h3>

              <div className="space-y-5 text-sm">
                {/* Companion plants */}
                {Array.isArray(care.companion_plants) && care.companion_plants.filter(Boolean).length > 0 && (
                  <div data-testid="platinum-companions">
                    <div className="flex items-center gap-2 mb-2">
                      <Leaf size={14} className="text-[#A5D6A7]" />
                      <h4 className="font-bold text-white">Companion plants</h4>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pl-6">
                      {care.companion_plants.filter(Boolean).map((p) => (
                        <span
                          key={p}
                          className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                          style={{ background: "rgba(76,175,80,0.22)", color: "#fff", border: "1px solid rgba(76,175,80,0.55)" }}
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                    <p className="text-[11px] mt-1.5 pl-6" style={{ color: "rgba(255,255,255,0.75)" }}>
                      Best neighbours when planted together.
                    </p>
                  </div>
                )}

                {/* Pet & human safety summary */}
                {care.toxicity && (
                  <div data-testid="platinum-safety">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle size={14} className={care.toxicity.toxic_to_pets || care.toxicity.toxic_to_humans ? "text-[#FF6B6B]" : "text-[#4CAF50]"} />
                      <h4 className="font-bold text-white">Pet & human safety</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pl-6">
                      <div
                        className="rounded-lg p-2.5"
                        style={{
                          background: care.toxicity.toxic_to_pets ? "rgba(211,47,47,0.22)" : "rgba(76,175,80,0.22)",
                          border: `1px solid ${care.toxicity.toxic_to_pets ? "rgba(211,47,47,0.6)" : "rgba(76,175,80,0.55)"}`,
                        }}
                      >
                        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "rgba(255,255,255,0.85)" }}>Pets</p>
                        <p className="text-sm font-extrabold mt-0.5" style={{ color: care.toxicity.toxic_to_pets ? "#FF8A8A" : "#A5D6A7" }}>
                          {care.toxicity.toxic_to_pets ? "Toxic" : "Pet-safe"}
                        </p>
                      </div>
                      <div
                        className="rounded-lg p-2.5"
                        style={{
                          background: care.toxicity.toxic_to_humans ? "rgba(211,47,47,0.22)" : "rgba(76,175,80,0.22)",
                          border: `1px solid ${care.toxicity.toxic_to_humans ? "rgba(211,47,47,0.6)" : "rgba(76,175,80,0.55)"}`,
                        }}
                      >
                        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "rgba(255,255,255,0.85)" }}>Humans</p>
                        <p className="text-sm font-extrabold mt-0.5" style={{ color: care.toxicity.toxic_to_humans ? "#FF8A8A" : "#A5D6A7" }}>
                          {care.toxicity.toxic_to_humans ? "Toxic if ingested" : "Safe"}
                        </p>
                      </div>
                    </div>
                    {care.toxicity.details && (
                      <p className="text-[12px] mt-2 pl-6 leading-relaxed" style={{ color: "rgba(255,255,255,0.92)" }}>
                        {care.toxicity.details}
                      </p>
                    )}
                  </div>
                )}

                {/* Year-round timeline */}
                {(care.fertiliser?.season || care.pruning?.when || care.propagation?.best_season) && (
                  <div data-testid="platinum-timeline">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={14} className="text-[#FFD54F]" />
                      <h4 className="font-bold text-white">Year-round timeline</h4>
                    </div>
                    <div className="pl-6 space-y-1.5 text-sm" style={{ color: "rgba(255,255,255,0.95)" }}>
                      {care.fertiliser?.season && (
                        <p>
                          <span className="font-bold" style={{ color: "#FFD54F" }}>Feed:</span> {care.fertiliser.season}
                          {care.fertiliser.frequency ? ` · ${care.fertiliser.frequency}` : ""}
                        </p>
                      )}
                      {care.pruning?.when && (
                        <p>
                          <span className="font-bold" style={{ color: "#FFD54F" }}>Prune:</span> {care.pruning.when}
                        </p>
                      )}
                      {care.propagation?.best_season && (
                        <p>
                          <span className="font-bold" style={{ color: "#FFD54F" }}>Propagate:</span> {care.propagation.best_season}
                          {care.propagation.difficulty ? ` · ${care.propagation.difficulty} difficulty` : ""}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Botanical reference */}
                <div
                  data-testid="platinum-botanical"
                  className="rounded-xl p-3"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,213,79,0.35)" }}
                >
                  <p className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: "#FFD54F" }}>Botanical reference</p>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.95)" }}>
                    <span className="font-semibold" style={{ color: "rgba(255,255,255,0.7)" }}>Scientific:</span> <i className="text-white">{top?.scientific_name || "—"}</i>
                  </p>
                  {top?.family && (
                    <p className="text-sm" style={{ color: "rgba(255,255,255,0.95)" }}>
                      <span className="font-semibold" style={{ color: "rgba(255,255,255,0.7)" }}>Family:</span> {top.family}
                    </p>
                  )}
                  {top?.confidence != null && (
                    <p className="text-sm" style={{ color: "rgba(255,255,255,0.95)" }}>
                      <span className="font-semibold" style={{ color: "rgba(255,255,255,0.7)" }}>AI confidence:</span> {Math.round(top.confidence)}%
                      {species.length > 1 && <span className="text-[11px] ml-1" style={{ color: "rgba(255,255,255,0.7)" }}> · {species.length - 1} alternative{species.length > 2 ? "s" : ""}</span>}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {user?.tier !== "free" && result.weather && (
        <WeatherCard weather={result.weather} />
      )}

      {user?.tier === "free" && result.climate_tip && (
        <div className="mt-4 glass-card p-5 relative overflow-hidden fade-up stagger-3" data-testid="climate-locked">
          <div className="absolute inset-0 backdrop-blur-md bg-black/40 z-10 flex flex-col items-center justify-center gap-3">
            <Sparkles size={24} className="text-[#FF8F00]" />
            <p className="text-sm font-semibold">Climate-specific advice on Premium</p>
            <button onClick={() => navigate("/subscription")} className="btn-amber text-xs px-4 py-2">Upgrade</button>
          </div>
          <p className="text-soft text-sm">{result.climate_tip}</p>
        </div>
      )}

      {user?.tier !== "free" && result.climate_tip && (
        <div className="mt-4 glass-card p-5 fade-up stagger-3"
             style={{ borderColor: "rgba(255,215,0,0.4)", boxShadow: "0 0 22px rgba(255,215,0,0.2)" }}>
          <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-1">For your climate</p>
          <p className="text-sm text-soft">{result.climate_tip}</p>
        </div>
      )}

      <div className="flex gap-3 mt-6">
        <button onClick={() => navigate("/identify")} className="btn-outline flex-1">Identify Another</button>
        <button onClick={() => navigate("/collection")} className="btn-primary flex-1 inline-flex items-center justify-center gap-1">
          Save to Collection <ChevronRight size={14} />
        </button>
      </div>
    </Shell>
  );
}
